<!--
  WidgetsSalePaymentBucket.vue

  Single-bucket sibling of WidgetsExpensePaymentBucket /
  WidgetsAdminCostPaymentBucket / WidgetsPurchasePaymentBucket.

  Records customer payments received against a Sale. Single MYR currency
  (the customer always pays locally). Each row carries Amount, PaymentDate,
  ReferenceNo, PaymentMethod, Remarks plus an optional receipt-scan upload.

  Header total is the running sum of payments received.

  Props:
    title       - bucket heading (e.g. "Customer Payments")
    payments    - array of SalePayment rows
    isReadOnly  - hides Add/Edit/Delete buttons when true

  Emits:
    add    -> payload for stocks/AddSalePayment/{stockId}
    update -> { id, payload } for stocks/UpdateSalePayment/{id}
    remove -> paymentId for stocks/RemoveSalePayment/{id}
-->
<template>
  <CCard class="mb-3">
    <CCardHeader class="d-flex justify-content-between align-items-center py-2">
      <strong>{{ title }}</strong>
      <strong class="text-primary">Total Received: RM {{ formattedTotal }}</strong>
    </CCardHeader>

    <CCardBody class="py-2">
      <CDataTable
        :items="payments || []"
        :fields="fields"
        small
        striped
        :noItemsView="{ noItems: 'No customer payments recorded' }"
      >
        <template #paymentDate="{ item }">
          <td>{{ displayDate(item.paymentDate) }}</td>
        </template>

        <template #amount="{ item }">
          <td class="text-right">RM {{ formatMoney(item.amount) }}</td>
        </template>

        <template #referenceNo="{ item }">
          <td><small>{{ item.referenceNo || '-' }}</small></td>
        </template>

        <template #paymentMethod="{ item }">
          <td>
            <CBadge :color="methodBadgeColor(item.paymentMethod)">
              {{ item.paymentMethod || '-' }}
            </CBadge>
          </td>
        </template>

        <template #document="{ item }">
          <td>
            <a
              v-if="item.document"
              :href="documentUrl(item.document)"
              target="_blank"
              :title="item.document.fileName"
            >
              {{ truncateFileName(item.document.fileName) }}
            </a>
            <small v-else class="text-muted">-</small>
          </td>
        </template>

        <template #actions="{ item }">
          <td class="py-1">
            <CButton
              v-if="!isReadOnly"
              size="sm"
              color="info"
              class="mr-1"
              @click="openEditModal(item)"
            >
              Edit
            </CButton>
            <CButton
              v-if="!isReadOnly"
              size="sm"
              color="danger"
              @click="$emit('remove', item.id)"
            >
              Delete
            </CButton>
          </td>
        </template>
      </CDataTable>
    </CCardBody>

    <CCardFooter v-if="!isReadOnly" class="py-2">
      <CButton size="sm" color="primary" @click="openAddModal">
        <i class="fas fa-plus"></i> Add Payment
      </CButton>
    </CCardFooter>

    <CModal
      :title="(editingId ? 'Edit' : 'Add') + ' Payment - ' + title"
      :show.sync="showModal"
      size="lg"
      :closeOnBackdrop="false"
    >
      <CForm>
        <CInput
          label="Payment Date"
          type="date"
          v-model="form.paymentDate"
          horizontal
        />

        <CInput
          label="Amount"
          prepend="RM"
          type="number"
          step="0.01"
          v-model.number="form.amount"
          horizontal
        />

        <CInput
          label="Reference No"
          v-model="form.referenceNo"
          horizontal
          description="Receipt #, cheque #, bank ref, etc."
        />

        <CSelect
          label="Payment Method"
          :options="methodOptions"
          :value.sync="form.paymentMethod"
          horizontal
        />

        <CInput
          label="Remarks"
          v-model="form.remarks"
          horizontal
        />

        <CRow class="mb-3">
          <CCol sm="3" class="text-sm-right">
            <label class="col-form-label">Receipt</label>
          </CCol>
          <CCol sm="9">
            <WidgetsUploadDocument
              :key="uploadKey"
              @uploaded="onDocumentUploaded"
              :multiple="false"
            />
            <div v-if="form.document" class="mt-2">
              <small class="text-success">
                <i class="fas fa-check-circle"></i>
                {{ form.document.fileName || 'Receipt uploaded' }}
              </small>
              <CButton
                size="sm"
                color="danger"
                variant="ghost"
                @click="clearDocument"
                class="ml-2"
              >
                <i class="fas fa-times"></i> Remove
              </CButton>
            </div>
          </CCol>
        </CRow>
      </CForm>

      <template #footer>
        <CButton color="secondary" @click="showModal = false">Cancel</CButton>
        <CButton color="primary" @click="onSaveClicked">
          {{ editingId ? 'Update' : 'Save' }}
        </CButton>
      </template>
    </CModal>
  </CCard>
</template>

<script>
import WidgetsUploadDocument from './WidgetsUploadDocument.vue'
import { formatMalaysiaDate } from '@/utils/dateUtil'

const METHOD_OPTIONS = [
  { value: '', label: '(select)' },
  { value: 'Cash', label: 'Cash' },
  { value: 'BankTransfer', label: 'Bank Transfer' },
  { value: 'Cheque', label: 'Cheque' },
  { value: 'Other', label: 'Other' }
]

const FIELDS = [
  { key: 'paymentDate', label: 'Date' },
  { key: 'amount', label: 'Amount (RM)', _classes: 'text-right' },
  { key: 'referenceNo', label: 'Reference' },
  { key: 'paymentMethod', label: 'Method' },
  { key: 'document', label: 'Receipt' },
  { key: 'actions', label: '', _style: 'width: 130px' }
]

export default {
  name: 'WidgetsSalePaymentBucket',
  components: { WidgetsUploadDocument },
  props: {
    title: { type: String, required: true },
    payments: { type: Array, default: () => [] },
    isReadOnly: { type: Boolean, default: false }
  },
  data () {
    return {
      methodOptions: METHOD_OPTIONS,
      fields: FIELDS,
      showModal: false,
      editingId: null,
      uploadKey: 0,
      form: this.emptyForm()
    }
  },
  computed: {
    total () {
      if (!Array.isArray(this.payments)) return 0
      return this.payments.reduce((s, p) => s + (Number(p.amount) || 0), 0)
    },
    formattedTotal () {
      return this.formatMoney(this.total)
    }
  },
  methods: {
    emptyForm () {
      return {
        paymentDate: new Date().toISOString().slice(0, 10),
        amount: 0,
        referenceNo: '',
        paymentMethod: '',
        remarks: '',
        documentId: null,
        document: null
      }
    },
    formatMoney (n) {
      const v = Number(n) || 0
      return v.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatDate (iso) {
      if (!iso) return ''
      return String(iso).slice(0, 10)
    },
    displayDate (iso) {
      return formatMalaysiaDate(iso)
    },
    truncateFileName (name) {
      if (!name) return 'View'
      if (name.length <= 24) return name
      const dot = name.lastIndexOf('.')
      const ext = dot > -1 ? name.slice(dot) : ''
      const head = name.slice(0, 18 - ext.length)
      return head + '…' + ext
    },
    methodBadgeColor (m) {
      switch (m) {
        case 'Cash': return 'success'
        case 'BankTransfer': return 'info'
        case 'Cheque': return 'warning'
        case 'Other': return 'secondary'
        default: return 'light'
      }
    },
    documentUrl (doc) {
      if (!doc) return '#'
      const apiBase = (window.config && window.config.API_URL) || ''
      const root = apiBase.replace(/\/api\/?$/, '')
      return root + '/api/Documents/File/' + doc.id
    },
    openAddModal () {
      this.editingId = null
      this.form = this.emptyForm()
      this.uploadKey++
      this.showModal = true
    },
    openEditModal (payment) {
      this.editingId = payment.id
      this.form = {
        paymentDate: this.formatDate(payment.paymentDate),
        amount: Number(payment.amount) || 0,
        referenceNo: payment.referenceNo || '',
        paymentMethod: payment.paymentMethod || '',
        remarks: payment.remarks || '',
        documentId: payment.documentId || null,
        document: payment.document || null
      }
      this.uploadKey++
      this.showModal = true
    },
    onDocumentUploaded (data) {
      if (data && Array.isArray(data.uploadedFiles) && data.uploadedFiles.length > 0) {
        const first = data.uploadedFiles[0]
        this.form.document = first
        this.form.documentId = first.id
      } else {
        this.form.document = null
        this.form.documentId = null
      }
    },
    clearDocument () {
      this.form.document = null
      this.form.documentId = null
      this.uploadKey++
    },
    onSaveClicked () {
      this.commit()
      this.showModal = false
    },
    buildPayload () {
      return {
        amount: Number(this.form.amount) || 0,
        paymentDate: this.form.paymentDate
          ? new Date(this.form.paymentDate).toISOString()
          : null,
        referenceNo: this.form.referenceNo || null,
        paymentMethod: this.form.paymentMethod || null,
        remarks: this.form.remarks || null,
        documentId: this.form.documentId || null
      }
    },
    commit () {
      const payload = this.buildPayload()
      if (this.editingId) {
        this.$emit('update', { id: this.editingId, payload })
      } else {
        this.$emit('add', payload)
      }
      this.editingId = null
    }
  }
}
</script>
